import { z } from 'zod'
import { err, ok } from '../utils/result.ts'
import type { AIModel } from '../types/enums.ts'
import type { Env } from '../types/env.ts'
import { BRAND_VISIBILITY_TOOL, TOOL_NAME } from './tool-schema.ts'
import { buildBatchPrompt } from './prompt.ts'
import type { AIIntegration, BatchMentionResult, BatchResponse } from './types.ts'
import { SENTIMENTS } from '../types/enums.ts'

const MODEL_IDS: Record<'gemini-2.5-flash' | 'gemini-2.5-pro', string> = {
  'gemini-2.5-flash': 'gemini-2.5-flash',
  'gemini-2.5-pro':   'gemini-2.5-pro',
}

const FunctionCallResultSchema = z.object({
  results: z.array(z.object({
    prompt_id:           z.string(),
    mentioned:           z.boolean(),
    rank_position:       z.number().int().nullable().optional(),
    sentiment:           z.enum(SENTIMENTS).nullable().optional(),
    context_snippet:     z.string().nullable().optional(),
    competitor_mentions: z.array(z.string()).optional(),
    ai_answer:           z.string().optional(),
  })),
})

const GeminiResponseSchema = z.object({
  candidates: z.array(z.object({
    content: z.object({
      parts: z.array(z.object({
        functionCall: z.object({
          name: z.string(),
          args: z.unknown(),
        }).optional(),
      })).min(1),
    }),
  })).min(1),
  usageMetadata: z.object({
    promptTokenCount: z.number().nullable().optional(),
    candidatesTokenCount: z.number().nullable().optional(),
  }).optional(),
})

type GeminiVariant = 'gemini-2.5-flash' | 'gemini-2.5-pro'

export function createGoogleIntegration(variant: GeminiVariant): AIIntegration {
  return {
    model: variant as AIModel,

    async queryBatch(params, env: Env) {
      const prompt = buildBatchPrompt(params)
      const modelId = MODEL_IDS[variant]
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:generateContent?key=${env.GOOGLE_AI_KEY}`

      // Gemini uses camelCase for function declarations, strip snake_case input_schema wrapper
      const geminiTool = {
        name: BRAND_VISIBILITY_TOOL.name,
        description: BRAND_VISIBILITY_TOOL.description,
        parameters: BRAND_VISIBILITY_TOOL.input_schema,
      }

      let res: Response
      try {
        res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            tools: [{ functionDeclarations: [geminiTool] }],
            toolConfig: { functionCallingConfig: { mode: 'ANY' } },
            generationConfig: { maxOutputTokens: 4096, temperature: 0.3 },
          }),
        })
      } catch (e) {
        return err(`Gemini network error: ${e instanceof Error ? e.message : String(e)}`)
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        return err(`Gemini API error ${res.status}: ${text}`)
      }

      let data: unknown
      try { data = await res.json() } catch {
        return err('Gemini returned invalid JSON')
      }

      const parsed = GeminiResponseSchema.safeParse(data)
      if (!parsed.success) return err(`Gemini response parse error: ${parsed.error.message}`)

      const part = parsed.data.candidates[0]?.content.parts.find((p) => p.functionCall?.name === TOOL_NAME)
      if (!part?.functionCall) return err('Gemini did not return a function call')

      const toolParsed = FunctionCallResultSchema.safeParse(part.functionCall.args)
      if (!toolParsed.success) return err(`Gemini function call args parse error: ${toolParsed.error.message}`)

      const promptMap = new Map(params.prompts.map((p) => [p.id, p.promptText]))

      const results: BatchMentionResult[] = toolParsed.data.results.map((r) => ({
        keywordId: r.prompt_id,
        promptText: promptMap.get(r.prompt_id) ?? '',
        mentioned: r.mentioned,
        rankPosition: r.rank_position ?? null,
        sentiment: r.sentiment ?? null,
        contextSnippet: r.context_snippet ?? null,
        competitorMentions: r.competitor_mentions ?? [],
        aiAnswer: r.ai_answer ?? null,
      }))

      const response: BatchResponse = {
        results,
        usage: {
          inputTokens: parsed.data.usageMetadata?.promptTokenCount ?? null,
          outputTokens: parsed.data.usageMetadata?.candidatesTokenCount ?? null,
        },
      }
      return ok(response)
    },
  }
}
